import React from "react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/ui";

const guidelines = [
  {
    title: "1. Treat Everyone with Respect",
    body: "Every user deserves kindness, dignity, and respect. Harassment, bullying, discrimination, intimidation, or hateful behavior of any kind will not be tolerated.",
  },
  {
    title: "2. Be Honest and Authentic",
    body: "Use genuine information and represent yourself truthfully. Building meaningful connections starts with honesty and trust.",
  },
  {
    title: "3. Keep Conversations Safe and Appropriate",
    body: "Communicate responsibly and respectfully. Avoid abusive, offensive, threatening, sexually explicit, or inappropriate content that may make others feel uncomfortable or unsafe.",
  },
  {
    title: "4. Respect Personal Boundaries",
    body: "Not everyone will respond, agree, or share the same interests. Respect the choices, privacy, and boundaries of other users at all times.",
  },
  {
    title: "5. Protect Privacy and Confidentiality",
    body: "Do not share another person\u2019s personal information, photos, conversations, or confidential details without their clear consent.",
  },
  {
    title: "6. No Fraud, Scams, or Misleading Activities",
    body: "ConnectPlus is a place for genuine relationships and opportunities. Any attempt to deceive, exploit, manipulate, or defraud other users is strictly prohibited.",
  },
  {
    title: "7. Use the Platform Responsibly",
    body: "Do not use ConnectPlus for illegal activities, harmful conduct, spam, unwanted promotions, or any activity that may negatively affect the community.",
  },
  {
    title: "8. Help Us Maintain a Safe Community",
    body: "If you encounter suspicious, abusive, or inappropriate behavior, please report it promptly. Together, we can protect the community and support one another.",
  },
  {
    title: "9. Respect Diversity and Inclusion",
    body: "Max & Sherry ConnectPlus welcomes people from different backgrounds, cultures, beliefs, and experiences. Treat everyone fairly and with understanding.",
  },
  {
    title: "10. Build Meaningful Connections",
    body: "Whether you are here for friendship, networking, relationships, opportunities, or services, we encourage you to engage with sincerity, empathy, and good intentions.",
  },
];

const sections = [
  {
    title: "1. Information We Collect",
    body: "We collect information you provide directly — your name, email, phone number, date of birth, photos, bio, and interests — plus information generated as you use Max & Sherry, such as matches, messages, and subscription status.",
  },
  {
    title: "2. How We Use Your Information",
    body: "Your information is used to create and verify your profile, match you with other members, process subscription payments, secure your account, and improve our service. We do not sell your personal information to third parties.",
  },
  {
    title: "3. Payment Information",
    body: "When you subscribe to a paid plan, payment is processed via Mpamba, Airtel Money, or bank transfer. We store only the minimum transaction details needed to confirm and reconcile your payment — we never store your mobile money PIN or full bank credentials.",
  },
  {
    title: "4. Data Sharing",
    body: "Your profile information (photos, bio, interests) is visible to other members as part of the matching experience. We do not share your personal data with advertisers or unrelated third parties.",
  },
  {
    title: "5. Data Retention & Account Deletion",
    body: "We retain your data for as long as your account is active. You may request permanent deletion of your account and associated data at any time from our Delete Account page — we will process your request and remove your data from our systems.",
  },
  {
    title: "6. Security",
    body: "We use industry-standard safeguards to protect your data in transit and at rest. No system is 100% secure, and we continuously work to strengthen our protections.",
  },
  {
    title: "7. Your Rights",
    body: "You have the right to access, correct, or delete your personal information, and to withdraw consent for optional data processing at any time by contacting us or using in-app tools.",
  },
  {
    title: "8. Children's Privacy",
    body: "Max & Sherry is intended for users aged 18 and older. We do not knowingly collect data from anyone under 18.",
  },
  {
    title: "9. Changes to This Policy",
    body: "We may update this policy from time to time. Continued use of Max & Sherry after changes take effect constitutes acceptance of the revised policy.",
  },
];

const toc = [
  { id: "guidelines", label: "Community Guidelines" },
  { id: "promise", label: "Our Promise to You" },
  { id: "privacy", label: "Privacy Policy" },
  ...sections.map((s, i) => ({ id: `privacy-${i + 1}`, label: s.title })),
  { id: "contact", label: "10. Contact Us" },
];

function goTo(e: React.MouseEvent, id: string) {
  e.preventDefault();
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function PrivacyPolicy() {
  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <Eyebrow>Legal</Eyebrow>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="text-[clamp(28px,4vw,44px)] font-bold tracking-[-1.2px] mb-4">
            Privacy Policy
          </h1>
        </Reveal>
        <Reveal delay={150}>
          <p className="text-sm text-muted mb-12">
            Last updated: September 2026
          </p>
        </Reveal>

        <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16">
          {/* Sticky table of contents (desktop only) */}
          <aside className="hidden lg:block">
            <div className="sticky top-[120px]">
              <p className="text-[13px] font-semibold text-black mb-3">
                On this page
              </p>
              <nav className="border-l border-black/10 max-h-[calc(100vh-180px)] overflow-y-auto">
                {toc.map((t) => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    onClick={(e) => goTo(e, t.id)}
                    className="block -ml-px pl-4 py-1.5 text-[13px] text-muted border-l border-transparent hover:text-rose hover:border-rose transition-colors duration-200 no-underline"
                  >
                    {t.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="max-w-[760px]">
            <Reveal delay={175}>
              <div className="mb-12 pb-10 border-b border-black/10">
                <p className="text-[15px] text-muted leading-relaxed">
                  Welcome to the Max & Sherry ConnectPlus App, a multipurpose
                  connection platform that brings together dating, friendship,
                  professional networking, communities and trusted services —
                  giving you an all-in-one modern, secure and meaningful digital
                  experience.
                </p>
              </div>
            </Reveal>

            <div id="guidelines" className="scroll-mt-[120px]">
              <Reveal>
                <h2 className="text-[clamp(22px,3vw,30px)] font-bold tracking-[-0.8px] mb-2">
                  Community Guidelines
                </h2>
                <p className="text-[15px] text-muted leading-relaxed mb-8">
                  At Max & Sherry ConnectPlus, we believe meaningful connections
                  thrive in an environment built on respect, trust, and safety.
                  By using this platform, you agree to uphold the following
                  standards:
                </p>
              </Reveal>
            </div>

            {guidelines.map((g, i) => (
              <Reveal key={g.title} delay={i * 20}>
                <div className="mb-8">
                  <h3 className="text-lg font-bold tracking-tight mb-2">
                    {g.title}
                  </h3>
                  <p className="text-[15px] text-muted leading-relaxed">
                    {g.body}
                  </p>
                </div>
              </Reveal>
            ))}

            <div id="promise" className="scroll-mt-[120px]">
              <Reveal>
                <div className="mb-12 pb-10 border-b border-black/10">
                  <div className="bg-blush rounded-[24px] p-6 lg:p-8">
                    <h3 className="text-lg font-bold tracking-tight mb-3">
                      Our Promise to You
                    </h3>
                    <p className="text-[15px] text-muted leading-relaxed mb-4">
                      Max & Sherry ConnectPlus is more than a platform — it is a
                      community. We are committed to fostering a safe,
                      respectful, and welcoming space where people can connect
                      with confidence, build meaningful relationships, discover
                      opportunities, and feel valued. Together, let us create a
                      community where every connection is built on respect,
                      trust, and genuine human care.
                    </p>
                    <p className="text-[15px] font-semibold text-rose-dark mb-1">
                      Meet. Network. Grow. Thrive.
                    </p>
                    <p className="text-sm text-muted italic">
                      This, too, is love!
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            <div id="privacy" className="scroll-mt-[120px]">
              <Reveal>
                <h2 className="text-[clamp(22px,3vw,30px)] font-bold tracking-[-0.8px] mb-6">
                  Privacy Policy
                </h2>
              </Reveal>
            </div>

            {sections.map((s, i) => (
              <Reveal key={s.title} delay={i * 20}>
                <div id={`privacy-${i + 1}`} className="mb-8 scroll-mt-[120px]">
                  <h3 className="text-lg font-bold tracking-tight mb-2">
                    {s.title}
                  </h3>
                  <p className="text-[15px] text-muted leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}

            <Reveal>
              <div
                id="contact"
                className="mt-12 pt-8 border-t border-black/10 scroll-mt-[120px]"
              >
                <h3 className="text-lg font-bold tracking-tight mb-2">
                  10. Contact Us
                </h3>
                <p className="text-[15px] text-muted leading-relaxed mb-6">
                  Questions about this policy or your data? Reach us at{" "}
                  <a
                    href="mailto:privacy@maxandsherry.mw"
                    className="text-rose hover:underline"
                  >
                    privacy@maxandsherry.mw
                  </a>
                  .
                </p>
                <Link
                  to="/delete-account"
                  className="inline-block bg-black text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-neutral-800 transition-colors duration-200 no-underline"
                >
                  Request account deletion
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
